import { FormulaFields, KeyQuality, KeyRoot } from 'services/store/ai/types';

export default {
  sideBar: {
    title: 'AI Music Generator',
    description: `Create AI music in seconds`,
    generate: 'Generate',
    errorMessage:
      'An unexpected error occurred. Please reload the page to try again.',
  },
  promptType: {
    generator: 'Generator',
    textToMusic: 'Text to Music',
  },
  textToMusic: {
    promptTitle: 'Text to Music Generation',
    instrumentalOnly: 'Instrumental only',
    loadingPrompt: '...Loading prompt',
    fallbackPrompt:
      'Create a calm, reflective ambient track that evokes peaceful solitude.',
  },
  inputs: {
    primaryGenre: 'Genre',
    secondaryGenre: 'Secondary Genre',
    minutes: 'Minutes',
    seconds: 'Seconds',
  },

  formulaFieldLabels: {
    [FormulaFields.Duration]: 'Duration',
    [FormulaFields.Instruments]: 'Instruments',
    [FormulaFields.GenreBlend]: 'Genre Blend',
    [FormulaFields.Energy]: 'Energy',
    [FormulaFields.Structure]: 'Structure',
    [FormulaFields.Bpm]: 'Tempo',
    [FormulaFields.Key]: 'Key',
    [FormulaFields.UserLoop]: 'Your Audio',
  },
  lockedField: {
    genre: 'Genre',
    microGenre: 'Sub Genre',
  },
  tagLabels: {
    blend: ' {{genreBlend}} Blend',
    energy: 'Energy',
    bpm: 'BPM',
    preview: 'Preview',
  },
  tag: { tagName: 'AI Generated' },
  duration: ' {{minutes}}:{{seconds}} ',
  durationSelector: {
    description: 'Min {{ min }}, Max {{ max }}',
  },
  bpmSelector: {
    description: 'Recommended: {{ low }}-{{ high }}',
  },
  trackResults: {
    errorMessage:
      'Based on a large number of requests, this song cannot be generated at the moment.',
    retry: 'Retry',
  },
  optional: 'Optional',
  addToLibrary: 'Add To Library',
  removeFromLibrary: 'Remove From Library',
  downloadSong: 'Download Track',
  energy: {
    low: 'Low',
    original: 'Original',
    high: 'High',
  },
  instruments: {
    DRUMS: 'Drums',
    BASS: 'Bass',
    KEYS: 'Keys',
    GUITAR: 'Guitar',
    SYNTH: 'Synth',
    STRINGS: 'Strings',
    'BRASS WOODWIND': 'Brass/Woodwind',
    BRASS_WOODWIND: 'Brass/Woodwind',
    'TONAL PERCUSSION': 'Tonal Percussion',
    TONAL_PERCUSSION: 'Tonal Percussion',
    PERCUSSION: 'Percussion',
    VOCALS: 'Vocals',
    SAMPLES: 'Samples',
    FX: 'FX',
  },
  key: {
    quality: {
      [KeyQuality.MINOR]: 'Minor',
      [KeyQuality.MAJOR]: 'Major',
    },
    root: {
      [KeyRoot.CSharp]: 'C#/Db',
      [KeyRoot.DSharp]: 'D#/Eb',
      [KeyRoot.FSharp]: 'F#/Gb',
      [KeyRoot.GSharp]: 'G#/Ab',
      [KeyRoot.BFlat]: 'Bb',
      [KeyRoot.C]: 'C',
      [KeyRoot.D]: 'D',
      [KeyRoot.E]: 'E',
      [KeyRoot.F]: 'F',
      [KeyRoot.G]: 'G',
      [KeyRoot.A]: 'A',
      [KeyRoot.B]: 'B',
    },
  },
  stepGuide: {
    titles: {
      buildYourSong: 'Build Your Song',
      generateTracks: 'Generate Tracks',
      saveAndDownload: 'Save and Download',
    },
    descriptions: {
      selecGenre: 'Select genre and other options to make your music unique.',
      getNewMusic: 'Get new music tracks fast and listen to your creations.',
      selectYourFavorites:
        'Select the ones you like, add to your library or download.',
    },
  },
  shareFormula: {
    tooltipTitle: 'Share this formula',
    heading: 'Share this song formula',
    description: 'Copy the link below:',
    copyButton: {
      title: 'Copy',
      titleCopied: 'Copied!',
    },
  },
  infoGuide: {
    helpGuide: 'Help Guide',
    back: 'Back',
  },
  upgrade: {
    description:
      'These tracks are previews. Upgrade to create full songs up to 30 minutes!',
    action: 'Upgrade',
    duration: 'Want full tracks?',
    microGenres: 'Unlock sub genres',
  },
  generationLimit: {
    title: 'AI Generation limit reached',
    descriptionFree:
      'Come back tomorrow to generate more, or upgrade now to get full access to Loudly!',
    descriptionPersonal:
      "You've reached your 30-day song generation limit. Enhance your experience by upgrading now to unlock additional song generations.",
    descriptionPro:
      'You have reached your 30-day limit. Come back in {{daysToReset}} days to generate more songs.',
    ctaPro: 'Ok, got it',
    upgrade: 'Upgrade',
    goBack: 'Maybe later',
  },
  editFormula: 'Edit Formula',
  userLoop: {
    info: 'Include your own audio clips!',
    uploadAudio: 'Upload Audio',
    audioAdded: 'Audio Added',
    freeUpgrade: {
      title: 'Upload limit reached',
      description:
        'Come back tomorrow to upload more, or upgrade now to get full access to Loudly!',
    },
    personalUpgrade: {
      title: 'Upload limit reached',
      description: `You've reached your 30-day limit for audio uploads. Enhance your experience by upgrading now to unlock additional uploads`,
    },
    proUpgrade: {
      title: 'Upload limit reached',
      description:
        'You have reached your 30 day limit, come back in {{daysToDownloadLimitReset}} days to upload more audio',
    },
    microphoneAccessMessage: 'Please allow Loudly access to your microphone',
  },
  userLoopUploadModal: {
    title: 'Upload Audio Clip',
    description:
      'Upon upload, we will process and integrate your audio into new music tracks.',
    instructions:
      'Must be an original audio clip between 1 - 60 seconds in duration.',
    agreement: `I certify that I own and have permissions or control all rights to any content I upload to Loudly. All uploaded audio content remains private to you. Loudly will not use uploaded audio content to train AI. By continuing, I acknowledge and agree to Loudly's Terms & Conditions.`,
    hint: 'Drag and Drop or <0>Click to Upload</0>',
    uploadButton: {
      upload: 'Upload',
      uploading: 'Uploading...',
      continue: 'Continue',
    },
    cancel: 'Cancel',
    back: 'Back',
    instrumentType: 'Instrument type*',
    fileSizeUnits: {
      emptyFile: 'Empty file',
      bytes: '{{size}} Bytes',
      kb: '{{size}} KB',
      mb: '{{size}} MB',
    },
    uploadStatus: {
      uploading: 'Uploading file...',
      uploaded: 'Upload complete',
    },
    validation: {
      invalidFileType: 'File format not supported. Please try another file.',
      audioFileTooLong:
        'The maximum song duration is 60 seconds. Please try another file.',
      audioFileTooShort:
        'The minimum song duration is 1 second. Please try another file.',
      tooManyFiles: 'Only one file at a time is allowed.',
      generic: 'This song cannot be used, please try another',
    },
    loopFileSelector: {
      upload: {
        title: 'Upload Audio',
        description: 'From your computer or device',
      },
      record: {
        title: 'Record Audio',
        description: 'Using your microphone',
      },
    },
  },
  userLoopSelector: {
    entropy: 'Entropy',
    preserve: 'Preserve',
    transform: 'Transform',
    upgradeButtonLabel: 'Upgrade',
    upgradeMessage: '{{remaining}} uploads remaining',
  },
};
